/**
 * Transition Detail Editor
 * 
 * Reusable component for editing transition details with tabbed interface
 * Can be used in tabs, cards, or dialogs
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Select, Card, message, Button, Tabs, Space } from 'antd';
import { SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { apiClient } from '../../../services/apiClient';
import { ArtifactStateTransitionType, DemandType, DemandFlowType } from '../../../types/enums';
import type { PrimeArtifactStateTransition } from '../../../types/primeModel';
import { TransitionParametersEditor, ValidationRulesEditor } from '../../Parameters';
import { RollupConfigurationPanel } from '../../Parameters/RollupConfigurationPanel';
import { PropertyChangesEditor } from '../PropertyChangesEditor';
import { TransitionParameters, ValidationRules, ArtifactGenerationParameters, PropertyChange } from '../../../types/transitionParameters';
import { ARTIFACT_GENERATION_CATALOG } from '../../../config/rollupParameterCatalog';
import { normalizeTransitionUpdate } from '../../../utils/transitionNormalization';
import './EditorStyles.css';

const { TextArea } = Input;

interface TransitionDetailEditorProps {
  transitionId: string;
  artifactId: string;
  activeTab?: string; // Which tab content to show
  onSave?: () => void;
  embedded?: boolean;
  onChange?: (values: any) => void;
}

export const TransitionDetailEditor: React.FC<TransitionDetailEditorProps> = ({
  transitionId,
  artifactId,
  activeTab = 'basic',
  onSave,
  embedded = false,
  onChange
}) => {
  // Removed verbose component render log
  
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [transitionData, setTransitionData] = useState<PrimeArtifactStateTransition | null>(null);
  const [transitionParams, setTransitionParams] = useState<TransitionParameters>({});
  const [validationRules, setValidationRules] = useState<ValidationRules>({});
  const [states, setStates] = useState<any[]>([]);
  const [processes, setProcesses] = useState<any[]>([]);
  const [currentArtifactProcesses, setCurrentArtifactProcesses] = useState<any[]>([]);
  const [artifactName, setArtifactName] = useState<string>('');
  const [processName, setProcessName] = useState<string>('');
  const [autoGeneratedName, setAutoGeneratedName] = useState<string>('');
  const [artifactProperties, setArtifactProperties] = useState<any[]>([]);

  useEffect(() => {
    if (!transitionId || !artifactId) {
      console.warn('[TransitionDetailEditor] Missing required props:', { transitionId, artifactId });
      setLoading(false);
      return;
    }
    loadTransitionData();
    loadArtifactProperties();
  }, [transitionId, artifactId]);
  
  // Auto-generate transition name when relevant fields change
  const generateTransitionName = (transitionType: string, fromStateName?: string, toStateName?: string) => {
    if (!artifactName) return '';
    
    const processPrefix = processName ? `${processName} ` : '';
    
    switch (transitionType) {
      case 'PROCESSITEM':
      case 'PROCESSITEM_PERSISTENT':
        if (fromStateName && toStateName) {
          return `${processPrefix}${artifactName} ${fromStateName} → ${toStateName}`;
        } else if (fromStateName) {
          return `${processPrefix}${artifactName} from ${fromStateName}`;
        } else if (toStateName) {
          return `${processPrefix}${artifactName} to ${toStateName}`;
        } else {
          return `${processPrefix}${artifactName}`;
        }
      case 'MATERIAL':
        if (fromStateName && toStateName) {
          return `${processPrefix}${artifactName} (Material) ${fromStateName} → ${toStateName}`;
        }
        return `${processPrefix}${artifactName} (Material)`;
      case 'RESOURCE':
        if (fromStateName && toStateName) {
          return `${processPrefix}${artifactName} (Resource) ${fromStateName} → ${toStateName}`;
        }
        return `${processPrefix}${artifactName} (Resource)`;
      case 'CREATION':
        if (toStateName) {
          return `${processPrefix}Create ${artifactName} (${toStateName})`;
        }
        return `${processPrefix}Create ${artifactName}`;
      default:
        return `${processPrefix}${artifactName}`;
    }
  };
  
  const loadTransitionData = async () => {
    try {
      setLoading(true);
      
      // Load artifact, states, and transitions in parallel
      // Use the artifact transitions endpoint directly since we know it works
      const [artifactRes, statesRes, transitionsRes] = await Promise.all([
        apiClient.get(`/prime-artifacts/${artifactId}`),
        apiClient.get(`/prime-artifacts/${artifactId}/states`).catch((error: any) => {
          console.error('[TransitionDetailEditor] Failed to load states:', error);
          return { data: [] }; // Return empty array on error
        }),
        apiClient.get(`/prime-artifacts/${artifactId}/transitions`)
      ]);
      
      // Find the specific transition in the list
      const listTransition = transitionsRes.data.find((t: any) => t.id === transitionId);
      let transitionRes: { data: any };
      if (!listTransition) {
        // Fallback: try the direct endpoint if not found in list
        try {
          const directRes = await apiClient.get(`/prime-transitions/transition/${transitionId}`);
          transitionRes = directRes;
        } catch (error: any) {
          throw new Error(`Transition ${transitionId} not found: ${error.response?.data?.detail || error.message}`);
        }
      } else {
        transitionRes = { data: listTransition };
      }

      // Always fetch full detail to ensure state_parameters and nested fields are present
      let detailRes = null;
      try {
        detailRes = await apiClient.get(`/prime-transitions/transition/${transitionId}`);
      } catch (error: any) {
        console.warn('[TransitionDetailEditor] Could not load transition detail, using list data:', error);
      }
      // Merge with existing transitionData so we don't lose fields the backend omits
      const detailTransition = detailRes?.data;
      const rawTransition = detailTransition || transitionRes.data;
      const prevTransition: any = transitionData || {};
      const listData: any = transitionRes.data || {};
      
      // Get state_parameters from API response (prefer state_parameters, fallback to transition_parameters for backward compatibility)
      const getStateParams = (data: any) => {
        return data?.state_parameters ?? data?.transition_parameters ?? data?.transition_params ?? data?.transitionParameters;
      };
      
      const fullTransition = {
        ...prevTransition,
        ...(rawTransition || {}),
        // prefer detail/list values when they exist, otherwise keep prior
        prime_process_ref:
          (rawTransition as any)?.prime_process_ref ??
          (rawTransition as any)?.prime_process_id ??
          listData?.prime_process_ref ??
          listData?.prime_process_id ??
          (rawTransition as any)?.process_ref ??
          (rawTransition as any)?.process_id ??
          prevTransition.prime_process_ref ??
          prevTransition.prime_process_id ??
          prevTransition.process_ref ??
          prevTransition.process_id,
        prime_process_id:
          (rawTransition as any)?.prime_process_id ??
          (rawTransition as any)?.prime_process_ref ??
          listData?.prime_process_id ??
          listData?.prime_process_ref ??
          (rawTransition as any)?.process_id ??
          (rawTransition as any)?.process_ref ??
          prevTransition.prime_process_id ??
          prevTransition.prime_process_ref ??
          prevTransition.process_id ??
          prevTransition.process_ref,
        // API returns state_parameters, but maintain transition_parameters for internal state
        state_parameters: getStateParams(rawTransition) ?? getStateParams(listData) ?? getStateParams(prevTransition) ?? transitionParams,
        transition_parameters: getStateParams(rawTransition) ?? getStateParams(listData) ?? getStateParams(prevTransition) ?? transitionParams,
      };
      
      // Load processes separately (use /prime-processes/all to get ALL processes)
      let allProcessesRes;
      try {
        allProcessesRes = await apiClient.get(`/prime-processes/all`);
      } catch (error: any) {
        console.warn('[TransitionDetailEditor] Failed to load all processes, using empty array:', error);
        allProcessesRes = { data: [] };
      }
      
      console.log('[TransitionDetailEditor] Loaded transition:', transitionRes.data);
      
      setArtifactName(artifactRes.data.name);
      // Ensure states is an array
      const statesArray = Array.isArray(statesRes.data) ? statesRes.data : [];
      setStates(statesArray);
      setProcesses(allProcessesRes.data || []);
      
      // Separate current artifact's processes for grouped display
      const currentArtifactProcs = (allProcessesRes.data || []).filter((p: any) => p.prime_artifact_ref === artifactId);
      setCurrentArtifactProcesses(currentArtifactProcs);
      
      setTransitionData(fullTransition);
      
      // Load process name if process is assigned
      if (fullTransition.prime_process_ref) {
        try {
          const processRes = await apiClient.get(`/prime-processes/${fullTransition.prime_process_ref}`);
          setProcessName(processRes.data.name);
        } catch (error) {
          console.warn('Failed to load process name:', error);
        }
      }
      
      // Set form values - handle both nested state objects and _ref/_id fields
      // The API can return: from_state.id, from_state_ref, or from_state_id
      const fromStateId = fullTransition.from_state?.id
        ? String(fullTransition.from_state.id)
        : (fullTransition.from_state_ref 
            ? String(fullTransition.from_state_ref)
            : (fullTransition.from_state_id ? String(fullTransition.from_state_id) : null));
      const toStateId = fullTransition.to_state?.id
        ? String(fullTransition.to_state.id)
        : (fullTransition.to_state_ref
            ? String(fullTransition.to_state_ref)
            : (fullTransition.to_state_id ? String(fullTransition.to_state_id) : null));

      // Convert enum values to uppercase for form (form expects uppercase, backend returns lowercase)
      const demandType = fullTransition.demand_type
        ? (typeof fullTransition.demand_type === 'string'
            ? fullTransition.demand_type.toUpperCase() as DemandType
            : fullTransition.demand_type)
        : DemandType.SUPPLY;
      const demandFlowType = fullTransition.demand_flow_type
        ? (typeof fullTransition.demand_flow_type === 'string'
            ? fullTransition.demand_flow_type.toUpperCase() as DemandFlowType
            : fullTransition.demand_flow_type)
        : DemandFlowType.PULL;

      const formValues = {
        name: fullTransition.name || '',
        description: fullTransition.description || '',
        from_state: fromStateId,
        to_state: toStateId,
        default_transition_type: fullTransition.default_transition_type || fullTransition.transition_type,
        demand_type: demandType,
        demand_flow_type: demandFlowType,
        prime_process_ref: fullTransition.prime_process_ref
          ? String(fullTransition.prime_process_ref)
          : (fullTransition.prime_process_id
              ? String(fullTransition.prime_process_id)
              : ((fullTransition as any)?.process_ref
                  ? String((fullTransition as any).process_ref)
                  : ((fullTransition as any)?.process_id
                      ? String((fullTransition as any).process_id)
                      : null))),
      };
      
      // Set form values - ensure states are loaded first
      form.setFieldsValue(formValues);
      
      // Resolve transition parameters, preserving property_changes from current state if backend omits them
      // API returns state_parameters, but check both for backward compatibility
      const loadedParams =
        fullTransition.state_parameters ||
        fullTransition.transition_parameters ||
        fullTransition.transition_params ||
        fullTransition.transitionParameters ||
        {};
      
      // If loaded params don't have property_changes but current state does, preserve them
      const resolvedTransitionParams = { ...loadedParams };
      
      // Check if loaded params are missing property_changes or have empty array
      const loadedHasPropertyChanges = 
        resolvedTransitionParams.property_changes && 
        Array.isArray(resolvedTransitionParams.property_changes) && 
        resolvedTransitionParams.property_changes.length > 0;
      
      const currentHasPropertyChanges = 
        transitionParams?.property_changes &&
        Array.isArray(transitionParams.property_changes) &&
        transitionParams.property_changes.length > 0;
      
      // If backend didn't return property_changes but we have them in current state, preserve them
      // BUT: Only preserve if loaded params are truly empty or missing property_changes
      // If loaded params have other data but no property_changes, we should still preserve
      if (!loadedHasPropertyChanges && currentHasPropertyChanges) {
        resolvedTransitionParams.property_changes = transitionParams.property_changes;
      }
      
      // If loaded params are completely empty but we have current state, use current state
      // This handles the case where backend returns {} or null for transition_parameters
      // BUT: Only do this if we're not in a fresh load (i.e., transitionParams has data)
      // On a fresh component mount, transitionParams will be empty, so we should use what the backend returns
      const loadedParamsIsEmpty = 
        Object.keys(resolvedTransitionParams).length === 0 || 
        (Object.keys(resolvedTransitionParams).length === 1 && resolvedTransitionParams.property_changes === undefined) ||
        (resolvedTransitionParams && typeof resolvedTransitionParams === 'object' && 
         !resolvedTransitionParams.property_changes && 
         Object.keys(resolvedTransitionParams).length === 0);
      
      // Only preserve current state if it has meaningful data AND loaded params are empty
      // This prevents overwriting backend data with empty state on fresh loads
      const currentStateHasData = transitionParams && 
        Object.keys(transitionParams).length > 0 &&
        (transitionParams.property_changes && 
         Array.isArray(transitionParams.property_changes) && 
         transitionParams.property_changes.length > 0);
      
      const finalParams = (loadedParamsIsEmpty && currentStateHasData)
        ? transitionParams
        : resolvedTransitionParams;
      
      // Ensure we always set objects, not null/undefined (backend requirement)
      const safeFinalParams = finalParams && typeof finalParams === 'object' && !Array.isArray(finalParams)
        ? finalParams
        : {};
      const safeValidationRules = fullTransition.validation_rules && typeof fullTransition.validation_rules === 'object' && !Array.isArray(fullTransition.validation_rules)
        ? fullTransition.validation_rules
        : {};
      
      setTransitionParams(safeFinalParams);
      setValidationRules(safeValidationRules);
      console.log('[TransitionDetailEditor] Data load complete. Final transition params:', finalParams);
      console.log('[TransitionDetailEditor] Property changes loaded:', safeFinalParams.property_changes);
      if (safeFinalParams.property_changes && Array.isArray(safeFinalParams.property_changes)) {
        safeFinalParams.property_changes.forEach((change: any, idx: number) => {
          if (change.source_artifact_id || change.aggregation_artifact_id) {
            console.log(`[TransitionDetailEditor] Property change ${idx} has artifact reference:`, {
              source_artifact_id: change.source_artifact_id,
              aggregation_artifact_id: change.aggregation_artifact_id,
              change_type: change.change_type
            });
          }
        });
      }
    } catch (error: any) {
      console.error('[TransitionDetailEditor] Error loading transition data:', error);
      console.error('[TransitionDetailEditor] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        transitionId,
        artifactId
      });
      message.error(`Failed to load transition data: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Preserve name if saving from a non-basic tab (no auto-generation)
      const resolvedName = (values.name ?? transitionData?.name ?? '').trim();
      if (!resolvedName) {
        message.error('Please enter a transition name');
        setSaving(false);
        return;
      }

      // Use existing from/to if not provided (e.g., when saving from Parameters tab)
      const transitionAny = transitionData as any;
      const resolvedFromState =
        values.from_state !== undefined
          ? values.from_state
          : transitionAny?.from_state_ref ?? transitionAny?.from_state_id ?? transitionAny?.from_state?.id ?? null;
      const resolvedToState =
        values.to_state !== undefined
          ? values.to_state
          : transitionAny?.to_state_ref ?? transitionAny?.to_state_id ?? transitionAny?.to_state?.id ?? null;

      // Preserve type/demand values when not present in current form (non-basic tab save)
      const resolvedTransitionType =
        values.default_transition_type ?? transitionAny?.default_transition_type ?? transitionAny?.transition_type;
      const resolvedDemandType = values.demand_type ?? transitionAny?.demand_type;
      const resolvedDemandFlowType = values.demand_flow_type ?? transitionAny?.demand_flow_type;
      const resolvedProcessRef =
        values.prime_process_ref ??
        transitionAny?.prime_process_ref ??
        transitionAny?.prime_process_id ??
        transitionAny?.process_ref ??
        transitionAny?.process_id ??
        null;

      // For CREATION transitions, backend expects from_state_ref to be null
      const isCreation = resolvedTransitionType === 'CREATION';

      // Build payload - normalization will ensure state_parameters and validation_rules are always objects
      const payload = normalizeTransitionUpdate({
        name: resolvedName,
        description: values.description ?? transitionData?.description,
        prime_artifact_ref: artifactId,
        from_state_ref: isCreation ? null : resolvedFromState,
        to_state_ref: resolvedToState,
        default_transition_type: resolvedTransitionType,
        demand_type: resolvedDemandType,
        demand_flow_type: resolvedDemandFlowType,
        state_parameters: transitionParams, // Will be normalized to {} if null/undefined/invalid
        validation_rules: validationRules, // Will be normalized to {} if null/undefined/invalid
        prime_process_ref: resolvedProcessRef === undefined || resolvedProcessRef === null || resolvedProcessRef === '' ? null : String(resolvedProcessRef),
        prime_process_id: resolvedProcessRef === undefined || resolvedProcessRef === null || resolvedProcessRef === '' ? null : String(resolvedProcessRef), // compatibility for backends expecting _id
      });
      
      // Debug: Log property changes being saved
      if (payload.state_parameters?.property_changes) {
        console.log('[TransitionDetailEditor] Saving property changes:', payload.state_parameters.property_changes);
        payload.state_parameters.property_changes.forEach((change: any, idx: number) => {
          if (change.source_artifact_id || change.aggregation_artifact_id) {
            console.log(`[TransitionDetailEditor] Property change ${idx} includes artifact reference:`, {
              source_artifact_id: change.source_artifact_id,
              aggregation_artifact_id: change.aggregation_artifact_id,
              change_type: change.change_type
            });
          }
        });
      }
      
      await apiClient.put(`/prime-artifacts/${artifactId}/transitions/${transitionId}`, payload);
      message.success('Transition updated successfully');
      // Refresh local data so name/states/parameters stay in sync after save
      await loadTransitionData();
      onSave?.();
    } catch (error: any) {
      console.error('Error saving transition:', error);
      message.error(`Failed to save transition: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = () => {
    if (embedded && onChange) {
      const values = form.getFieldsValue();
      onChange({ ...values, transition_parameters: transitionParams, validation_rules: validationRules });
    }
  };

  const loadArtifactProperties = async () => {
    if (!artifactId) return;
    
    try {
      const response = await apiClient.get(`/prime-artifacts/${artifactId}/properties`);
      
      // Handle different response formats
      let properties: any[] = [];
      if (Array.isArray(response.data)) {
        properties = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        properties = response.data.data;
      } else if (response.data && Array.isArray(response.data.properties)) {
        properties = response.data.properties;
      } else if (response.data && typeof response.data === 'object' && 'items' in response.data && Array.isArray(response.data.items)) {
        properties = response.data.items;
      } else if (Array.isArray(response)) {
        properties = response;
      } else if (response.data && typeof response.data === 'object') {
        // If response.data is an object but not an array, log it for debugging
        console.warn('[TransitionDetailEditor] Unexpected response.data structure:', response.data);
        // Try to extract any array-like structure
        const possibleArrays = Object.values(response.data).filter(Array.isArray);
        if (possibleArrays.length > 0) {
          properties = possibleArrays[0] as any[];
          console.log('[TransitionDetailEditor] Found array in response.data:', possibleArrays[0]);
        }
      }
      
      setArtifactProperties(properties);
      console.log('[TransitionDetailEditor] Loaded properties:', properties.length, properties);
    } catch (error: any) {
      console.error('[TransitionDetailEditor] Failed to load properties:', error);
      console.error('[TransitionDetailEditor] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      // Don't show error message - properties are optional
      setArtifactProperties([]);
    }
  };

  const handleGenerateProcess = async () => {
    try {
      setGenerating(true);
      const hasExistingProcess = !!form.getFieldValue('prime_process_ref');
      
      const response = await apiClient.post(`/prime-transitions/${transitionId}/generate-process`, {
        allow_replace: true  // Always allow replacing existing process
      });
      
      const successMsg = hasExistingProcess 
        ? 'Process regenerated successfully' 
        : 'Process generated successfully';
      message.success(successMsg);
      
      // Reload transition data to get the new process
      await loadTransitionData();
      
      // Notify parent to refresh
      onSave?.();
    } catch (error: any) {
      console.error('[TransitionDetailEditor] Error generating process:', error);
      console.error('[TransitionDetailEditor] Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      message.error(`Failed to generate process: ${errorMsg}`, 8); // Show for 8 seconds
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <div className="editor-loading">Loading transition data...</div>;
  }

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'transition-basic':
      case 'basic':
        return (
            <Form
              form={form}
              layout="vertical"
              onValuesChange={handleFormChange}
            >
              <Form.Item
                label="Transition Name"
                name="name"
                tooltip="Enter a transition name (no auto-generation)"
                rules={[{ required: true, message: 'Please enter a transition name' }]}
              >
                <Input placeholder="Enter transition name" />
              </Form.Item>

              <Form.Item
                label="Description"
                name="description"
              >
                <TextArea rows={3} placeholder="Enter transition description" />
              </Form.Item>

              <Form.Item
                label="From State"
                name="from_state"
                rules={[
                  {
                    validator: async (_, value) => {
                      const transitionType = form.getFieldValue('default_transition_type');
                      if (transitionType !== 'CREATION' && !value) {
                        return Promise.reject(new Error('Please select a from state'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Select 
                  placeholder="Select starting state"
                  allowClear
                  disabled={form.getFieldValue('default_transition_type') === 'CREATION'}
                >
                  {states.map(state => (
                    <Select.Option key={state.id} value={state.id}>
                      {state.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="To State"
                name="to_state"
                rules={[{ required: true, message: 'Please select a to state' }]}
              >
                <Select 
                  placeholder="Select destination state"
                >
                  {states.map(state => (
                    <Select.Option key={state.id} value={state.id}>
                      {state.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Transition Type"
                name="default_transition_type"
                rules={[{ required: true, message: 'Please select a transition type' }]}
              >
                <Select 
                  placeholder="Select transition type"
                  onChange={(value) => {
                    if (value === 'CREATION') {
                      form.setFieldsValue({ from_state: null });
                    }
                  }}
                >
                  <Select.Option value="PROCESSITEM">Process Item</Select.Option>
                  <Select.Option value="PROCESSITEM_PERSISTENT">Process Item (Persistent)</Select.Option>
                  <Select.Option value="MATERIAL">Material</Select.Option>
                  <Select.Option value="RESOURCE">Resource</Select.Option>
                  <Select.Option value="CREATION">Creation</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Demand Type"
                name="demand_type"
              >
                <Select placeholder="Select demand type">
                  <Select.Option value="SUPPLY">Supply</Select.Option>
                  <Select.Option value="DEMAND">Demand</Select.Option>
                  <Select.Option value="TARGET">Target</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Demand Flow Type"
                name="demand_flow_type"
              >
                <Select placeholder="Select demand flow type">
                  <Select.Option value="PUSH">Push</Select.Option>
                  <Select.Option value="PULL">Pull</Select.Option>
                  <Select.Option value="DRUM">Drum</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    <span>Process</span>
                    <Button
                      type="link"
                      size="small"
                      icon={<ThunderboltOutlined />}
                      onClick={handleGenerateProcess}
                      loading={generating}
                      style={{ padding: '0 4px', height: 'auto' }}
                      title={form.getFieldValue('prime_process_ref') ? 'Generate new process (replaces current)' : 'Generate new process'}
                    >
                      {form.getFieldValue('prime_process_ref') ? 'Regenerate' : 'Generate New'}
                    </Button>
                  </Space>
                }
                name="prime_process_ref"
              >
                <Select 
                  placeholder="Type to search processes..." 
                  allowClear
                  showSearch
                  filterOption={(input, option: any) => {
                    // Search by the process name
                    if (option?.children) {
                      return String(option.children).toLowerCase().includes(input.toLowerCase());
                    }
                    return false;
                  }}
                  onChange={(value) => {
                    // Update process name without changing transition name
                    const selectedProcess = processes.find(p => p.id === value);
                    setProcessName(selectedProcess?.name || '');
                  }}
                >
                  {/* Current Artifact's Processes First */}
                  {currentArtifactProcesses.length > 0 && (
                    <Select.OptGroup label={`${artifactName} Processes`}>
                      {currentArtifactProcesses.map(process => (
                        <Select.Option key={process.id} value={process.id}>
                          {process.name}
                        </Select.Option>
                      ))}
                    </Select.OptGroup>
                  )}
                  
                  {/* All Other Processes */}
                  {processes.filter(p => p.prime_artifact_ref !== artifactId).length > 0 && (
                    <Select.OptGroup label="All Processes">
                      {processes
                        .filter(p => p.prime_artifact_ref !== artifactId)
                        .map(process => (
                          <Select.Option key={process.id} value={process.id}>
                            {process.name} (from other artifact)
                          </Select.Option>
                        ))}
                    </Select.OptGroup>
                  )}
                </Select>
              </Form.Item>
            </Form>
        );
      
      case 'transition-parameters':
      case 'parameters':
        return (
          <PropertyChangesEditor
            propertyChanges={transitionParams.property_changes || []}
            onChange={(changes: PropertyChange[]) => {
              setTransitionParams({
                ...transitionParams,
                property_changes: changes
              });
            }}
            artifactProperties={artifactProperties}
          />
        );
      
      case 'transition-validation':
      case 'validation':
        return (
          <ValidationRulesEditor
            validationRules={validationRules}
            onChange={setValidationRules}
            artifactProperties={artifactProperties}
          />
        );
      
      case 'transition-rollup':
      case 'rollup':
        return (
          <RollupConfigurationPanel
            rollupParams={transitionParams.rollup_parameters || {}}
            onChange={(rollupParams) => {
              setTransitionParams({
                ...transitionParams,
                rollup_parameters: rollupParams
              });
            }}
          />
        );
      
      case 'transition-generation':
      case 'generation': {
        const genParams = transitionParams.artifact_generation_parameters || {};
        const generationMode = genParams.artifact_generation_mode?.value as 'singleton' | 'sequential' | 'concurrent' | undefined;
        
        return (
          <div className="editor-section">
            <p style={{ marginBottom: 16, color: '#6b7280' }}>
              Configure how artifacts are generated through this transition (CREATION transitions only)
            </p>
            
            {/* Generation Mode */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Generation Mode:</label>
              <Select
                style={{ width: '100%' }}
                placeholder="Select generation mode"
                value={generationMode}
                onChange={(value) => {
                  const updated = {
                    ...transitionParams,
                    artifact_generation_parameters: {
                      ...genParams,
                      artifact_generation_mode: {
                        type: 'string' as const,
                        value: value as 'singleton' | 'sequential' | 'concurrent',
                        description: 'Artifact generation mode'
                      }
                    }
                  };
                  // Clear mode-specific params when mode changes
                  if (value !== 'singleton') {
                    delete updated.artifact_generation_parameters.replacement_behavior;
                  }
                  if (value !== 'sequential') {
                    delete updated.artifact_generation_parameters.completion_required;
                    delete updated.artifact_generation_parameters.max_active_items;
                  }
                  if (value !== 'concurrent') {
                    delete updated.artifact_generation_parameters.max_total_items;
                  }
                  setTransitionParams(updated);
                }}
              >
                {ARTIFACT_GENERATION_CATALOG.artifact_generation_mode.options?.map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label} - {opt.description}
                  </Select.Option>
                ))}
              </Select>
            </div>

            {/* Singleton-specific parameters */}
            {generationMode === 'singleton' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Replacement Behavior:
                </label>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select replacement behavior"
                  value={genParams.replacement_behavior?.value}
                  onChange={(value) => {
                    setTransitionParams({
                      ...transitionParams,
                      artifact_generation_parameters: {
                        ...genParams,
                        replacement_behavior: {
                          type: 'string' as const,
                          value: value as 'archive_previous' | 'delete_previous' | 'error_if_exists',
                          description: 'What to do if artifact already exists'
                        }
                      }
                    });
                  }}
                >
                  {ARTIFACT_GENERATION_CATALOG.replacement_behavior.options?.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label} - {opt.description}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {/* Sequential-specific parameters */}
            {generationMode === 'sequential' && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    Completion Required:
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={genParams.completion_required?.value === true ? 'true' : genParams.completion_required?.value === false ? 'false' : undefined}
                    onChange={(value) => {
                      setTransitionParams({
                        ...transitionParams,
                        artifact_generation_parameters: {
                          ...genParams,
                          completion_required: {
                            type: 'boolean' as const,
                            value: value === 'true',
                            description: 'Must previous item complete before creating next'
                          }
                        }
                      });
                    }}
                  >
                    <Select.Option value="true">Yes - Enforce strict sequential order</Select.Option>
                    <Select.Option value="false">No - Allow overlap</Select.Option>
                  </Select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    Max Active Items:
                  </label>
                  <InputNumber
                    style={{ width: '100%' }}
                    value={genParams.max_active_items?.value ?? undefined}
                    onChange={(value: number | null) => {
                      setTransitionParams({
                        ...transitionParams,
                        artifact_generation_parameters: {
                          ...genParams,
                          max_active_items: {
                            type: 'number' as const,
                            value: value ?? null,
                            description: 'Maximum simultaneously active items'
                          }
                        }
                      });
                    }}
                    placeholder="e.g., 1 for strict sequential"
                    min={1}
                  />
                </div>
              </>
            )}

            {/* Concurrent-specific parameters */}
            {generationMode === 'concurrent' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Max Total Items (Optional):
                </label>
                <InputNumber
                  style={{ width: '100%' }}
                  value={genParams.max_total_items?.value ?? undefined}
                  onChange={(value: number | null) => {
                    setTransitionParams({
                      ...transitionParams,
                      artifact_generation_parameters: {
                        ...genParams,
                        max_total_items: {
                          type: 'number' as const,
                          value: value ?? null,
                          description: 'Optional limit on total items'
                        }
                      }
                    });
                  }}
                  placeholder="Leave empty for unlimited"
                  min={1}
                />
                <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  Optional cap on total number of items. Leave empty for unlimited.
                </p>
              </div>
            )}

            {!generationMode && (
              <p style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
                Select a generation mode to configure additional parameters
              </p>
            )}
          </div>
        );
      }
      
      default:
        return <div>Select a tab to view content</div>;
    }
  };

  return (
    <div className="transition-detail-editor">
      <Card
        title={`Transition: ${transitionData?.name || 'Unknown'}`}
        className="editor-card"
        extra={
          !embedded && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Save Changes
            </Button>
          )
        }
      >
        {renderTabContent()}
      </Card>
    </div>
  );
};

